import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Calendar,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  User,
  X,
  Wallet,
  Building2,
  Sparkles,
  Sun,
  Moon,
  UserPlus,
  Globe
} from 'lucide-react';

const LOGO_URL = '/logo.webp';

// The marketing site. Shares the .clayworks.space session cookie with this
// app, so this is a plain link — the visitor stays signed in on arrival and
// its header picks them up automatically.
const MAIN_SITE_URL = process.env.REACT_APP_MAIN_SITE_URL || 'https://clayworks.space';

const Navbar = () => {
  const { user, logout, isAuthenticated, isAdmin, isCenterManager, isGuest } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getDashboardLink = () => {
    if (isAdmin) return '/admin';
    if (isCenterManager) return '/manager';
    if (user?.role === 'sales') return '/sales';
    if (user?.role === 'founders') return '/founders';
    if (user?.role === 'finance_audit') return '/finance';
    if (user?.role === 'manager') return '/approvals';
    if (user?.role === 'finance') return '/finance-approvals';
    if (user?.role === 'landlord') return '/landlord';
    if (user?.role === 'bd_transactions') return '/bd-transactions';
    return '/dashboard';
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`sticky top-0 z-50 backdrop-blur-xl border-b ${
      isDark 
        ? 'bg-[#0a0b0d]/95 border-[#1e2128]' 
        : 'bg-[#f7f3e8]/95 border-[#e5dfd0]'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to={getDashboardLink()} className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="Clayworks" className="h-9 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <Link 
              to="/venues" 
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isActive('/venues') 
                  ? 'bg-primary/10 text-primary' 
                  : `text-muted-foreground hover:text-foreground ${isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]'}`
              }`}
              data-testid="venues-link"
            >
              <Building2 className="w-4 h-4 inline mr-2" />
              Venues
            </Link>
            <Link 
              to="/booking" 
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isActive('/booking') 
                  ? 'bg-primary/10 text-primary' 
                  : `text-muted-foreground hover:text-foreground ${isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]'}`
              }`}
              data-testid="booking-link"
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Book Now
            </Link>

            <Link
              to="/ecosystem"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isActive('/ecosystem')
                  ? 'bg-primary/10 text-primary'
                  : `text-muted-foreground hover:text-foreground ${isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]'}`
              }`}
              data-testid="ecosystem-link"
            >
              <Sparkles className="w-4 h-4 inline mr-2" />
              Ecosystem
            </Link>

            <a
              href={MAIN_SITE_URL}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all text-muted-foreground hover:text-foreground ${
                isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]'
              }`}
              data-testid="main-site-link"
            >
              <Globe className="w-4 h-4 inline mr-2" />
              Main Site
            </a>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all ${
                isDark 
                  ? 'hover:bg-[#13161a] text-muted-foreground hover:text-foreground' 
                  : 'hover:bg-[#efe9da] text-muted-foreground hover:text-foreground'
              }`}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            {isAuthenticated && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 px-3 ml-2" data-testid="user-menu-trigger">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium max-w-[100px] truncate">{user?.name}</span>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className={`w-56 ${isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-[#faf8f3] border-[#e5dfd0]'}`} align="end">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                      <p className="text-xs text-primary capitalize">{user?.role?.replace('_', ' ')}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className={isDark ? 'bg-[#1e2128]' : 'bg-[#e5dfd0]'} />
                  <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="dashboard-menu-item" className={isDark ? 'focus:bg-[#1e2128]' : 'focus:bg-[#efe9da]'}>
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/bookings')} data-testid="bookings-menu-item" className={isDark ? 'focus:bg-[#1e2128]' : 'focus:bg-[#efe9da]'}>
                    <Calendar className="mr-2 h-4 w-4" />
                    My Bookings
                  </DropdownMenuItem>
                  {user?.user_type === 'internal' && (
                    <DropdownMenuItem onClick={() => navigate('/credits')} data-testid="credits-menu-item" className={isDark ? 'focus:bg-[#1e2128]' : 'focus:bg-[#efe9da]'}>
                      <Wallet className="mr-2 h-4 w-4" />
                      Credits
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/invoices')} data-testid="invoices-menu-item" className={isDark ? 'focus:bg-[#1e2128]' : 'focus:bg-[#efe9da]'}>
                    <FileText className="mr-2 h-4 w-4" />
                    Invoices
                  </DropdownMenuItem>
                  {isGuest && (
                    <DropdownMenuItem onClick={() => navigate('/register')} data-testid="guest-create-account-menu-item" className={`text-primary ${isDark ? 'focus:bg-[#1e2128]' : 'focus:bg-[#efe9da]'}`}>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Create a full account
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className={isDark ? 'bg-[#1e2128]' : 'bg-[#e5dfd0]'} />
                  <DropdownMenuItem onClick={handleLogout} className={`text-destructive ${isDark ? 'focus:bg-[#1e2128]' : 'focus:bg-[#efe9da]'} focus:text-destructive`} data-testid="logout-menu-item">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center gap-2 md:hidden">
            {/* Mobile Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all ${
                isDark 
                  ? 'hover:bg-[#13161a]' 
                  : 'hover:bg-[#efe9da]'
              }`}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            <button 
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]'}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className={`md:hidden py-4 border-t animate-fade-in ${isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]'}`}>
            <div className="flex flex-col gap-2">
              <Link 
                to="/venues" 
                className={`px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Building2 className="w-4 h-4" />
                Venues
              </Link>
              <Link 
                to="/booking" 
                className={`px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Calendar className="w-4 h-4" />
                Book Now
              </Link>
              <a
                href={MAIN_SITE_URL}
                className={`px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Globe className="w-4 h-4" />
                Main Site
              </a>
              {isAuthenticated && (
                <>
                  <Link 
                    to={getDashboardLink()} 
                    className={`px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]'}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </Link>
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className={`px-4 py-3 rounded-lg text-sm font-medium text-destructive text-left flex items-center gap-2 ${isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]'}`}
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
